import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { hydroUnitsAPI, apiUtils } from '../services/api';
import { useSocket } from '../contexts/SocketContext';
import { useUnitSettings } from '../contexts/UnitSettingsContext';
import HourPresets from './HourPresets';
import { windowMinutes, formatMinutes } from '../config/relayWindow';
import { PiClock, PiHandPointing, PiPause } from 'react-icons/pi';
import { SYSTEM_IDS } from '../config/systems';
import { controllerLink } from '../config/freshness';

/*
 * Grow-light control for the Dashboard: one row per enabled unit, each with its
 * own state, control mode and on/off window. `compact` shows one summary line per
 * rack (state, mode, window) and opens the editor only for the rack tapped, since
 * lights are checked at a glance far more often than they are changed.
 *
 * Timers are evaluated by the backend scheduler only (30s, IST). Nothing here
 * decides whether a light should be on — it posts the operator's intent and
 * renders what the server reports back, so the displayed state is always the
 * state the ESP32 will poll.
 */

const DEFAULT_WINDOW = { on: '08:00', off: '20:00' };

const CONFIRM_OK = '#40916C';
const CONFIRM_WARN = '#E9C46A';
const CONFIRM_BAD = '#E63946';
const CONFIRM_MUTED = '#6C757D';

function relayAge(seconds) {
  if (seconds == null) return 'never';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

function RelayConfirmation({ report }) {
  // No report at all: the controller for this unit has never checked in, or not
  // within the staleness window. Said plainly rather than shown as a failure,
  // because an un-deployed board is not a broken relay.
  if (!report) {
    return (
      <Confirm $color={CONFIRM_MUTED} title="No relay controller has reported for this unit. The dashboard cannot tell whether the light actually switched.">
        <ConfirmDot $color={CONFIRM_MUTED} />no controller
      </Confirm>
    );
  }

  if (!report.online) {
    return (
      <Confirm $color={CONFIRM_BAD} title={`Controller last seen ${relayAge(report.age_seconds)}. The state shown is what was commanded, not what the hardware is doing.`}>
        <ConfirmDot $color={CONFIRM_BAD} />controller offline
      </Confirm>
    );
  }

  if (!report.confirmed || !report.applied) {
    return (
      <Confirm $color={CONFIRM_WARN} title={`Commanded ${report.commanded || '?'}, controller reports ${report.actual || '?'}. The command has not taken effect on the hardware.`}>
        <ConfirmDot $color={CONFIRM_WARN} />not confirmed
      </Confirm>
    );
  }

  return (
    <Confirm $color={CONFIRM_OK} title={`Controller confirms ${report.actual} as of ${relayAge(report.age_seconds)}. Note: this is a readback of the controller's output pin, which proves the command reached the GPIO - not that the contact closed or current flowed.`}>
      <ConfirmDot $color={CONFIRM_OK} />confirmed
    </Confirm>
  );
}

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${props => (props.$bare ? 'auto minmax(0, 1fr)' : 'minmax(80px, 1fr) auto auto')};
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  /* A unit in maintenance is shown but not actionable — same 45% treatment the
     unit tiles use, so "cannot touch this" reads the same way page-wide. */
  opacity: ${props => (props.dimmed ? 0.45 : 1)};

  &:last-child { border-bottom: none; }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr auto;
    row-gap: ${props => props.theme.spacing.sm};
  }
`;

/* Whether the hardware confirmed the command, distinct from what we asked for.
   The relay controller reports its own view of each channel, and comparing that
   with the commanded state is the only way to tell a toggle that reached the
   hardware from one that silently did not. */
const Confirm = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.$color};
  cursor: help;
  white-space: nowrap;
`;

const ConfirmDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

const UnitCell = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${props => props.theme.spacing.sm};
  min-width: 0;
`;

const UnitName = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
`;

/* State is text first. Colour alone would be the only signal otherwise, and the
   success green on surface is too low-contrast to carry the meaning by itself. */
const StatePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${props => (props.isOn ? props.theme.colors.success : props.theme.colors.textMuted)};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${props => (props.isOn ? props.theme.colors.success : props.theme.colors.textMuted)};
  }
`;

const ModeSwitch = styled.div`
  display: inline-flex;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: 2px;
`;

const ModeButton = styled.button`
  padding: 3px 9px;
  border: none;
  border-radius: 3px;
  font-size: 0.68rem;
  font-weight: 600;
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  background: ${props => {
    if (!props.active) return 'transparent';
    return props.mode === 'manual' ? props.theme.colors.warning : props.theme.colors.primary;
  }};
  color: ${props => (props.active ? props.theme.colors.textInverse : props.theme.colors.textMuted)};

  &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: ${props => (props.$bare ? 'flex-start' : 'flex-end')};
  gap: ${props => props.theme.spacing.sm};
`;

const TimeInput = styled.input`
  padding: 4px 6px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 4px;
  font-size: 0.78rem;
  font-family: inherit;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};

  &:focus { border-color: ${props => props.theme.colors.primary}; outline: none; }
  &:disabled { color: ${props => props.theme.colors.textMuted}; }
`;

const TimeSep = styled.span`
  font-size: 0.72rem;
  color: ${props => props.theme.colors.textMuted};
`;

const SaveButton = styled.button`
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.textInverse};

  &:hover:not(:disabled) { background: ${props => props.theme.colors.primaryHover}; }
  &:disabled { background: ${props => props.theme.colors.border}; color: ${props => props.theme.colors.textMuted}; cursor: not-allowed; }
`;

const Toggle = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 22px;
  border: none;
  border-radius: 11px;
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  background: ${props => (props.isOn ? props.theme.colors.success : props.theme.colors.border)};

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => (props.isOn ? '20px' : '2px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: ${props => props.theme.transitions.fast};
  }

  &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

const Note = styled.div`
  font-size: 0.7rem;
  color: ${props => props.theme.colors.textMuted};
  margin-top: ${props => props.theme.spacing.sm};
`;

const ErrorNote = styled(Note)`
  color: ${props => props.theme.colors.danger};
`;

// Spans the whole row, under the unit / mode / time inputs.
const PresetCell = styled.div`
  grid-column: 1 / -1;
  margin-top: -${props => props.theme.spacing.xs};
`;

const WindowLabel = styled.span`
  font-size: 0.72rem;
  color: ${props => props.theme.colors.textMuted};
  white-space: nowrap;
`;

/* Compact mode: the summary line is the button that opens the editor. */
const Item = styled.div`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  &:last-child { border-bottom: none; }
  /* The editor's own row border would double up with the item's. */
  & > div:last-child:not(button) { border-bottom: none; }
`;

const Summary = styled.button`
  display: grid;
  grid-template-columns: minmax(84px, auto) 44px 1fr auto auto;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
  padding: 7px 4px;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  opacity: ${props => (props.dimmed ? 0.45 : 1)};
  border-radius: 6px;

  &:hover { background: ${props => props.theme.colors.surfaceHover}; }
  &:focus-visible { outline: 2px solid ${props => props.theme.colors.primary}; outline-offset: 1px; }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: minmax(70px, auto) 40px 1fr auto;
    .confirm { display: none; }
  }
`;

const SummaryWhen = styled.span`
  font-size: 0.78rem;
  color: ${props => (props.$manual ? props.theme.colors.text : props.theme.colors.textSecondary)};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  white-space: nowrap;
`;

// IST wall clock for a unix time, e.g. "14:20".
const clock = ts => new Date((ts + 19800) * 1000).toISOString().slice(11, 16);

const LightRow = ({ unitId, name, unit, report, dimmed, onChanged, onError, bare = false }) => {
  const mode = unit?.control_modes?.lights || 'timer';
  const state = unit?.relays?.lights || 'OFF';
  const isOn = state === 'ON';
  const saved = unit?.schedule?.lights || DEFAULT_WINDOW;

  const [onTime, setOnTime] = useState(saved.on || DEFAULT_WINDOW.on);
  const [offTime, setOffTime] = useState(saved.off || DEFAULT_WINDOW.off);
  const [busy, setBusy] = useState(false);

  // Re-sync the inputs when the server's window changes (another client saved,
  // or the first fetch landed after mount). Local edits are only kept while
  // they differ from what is stored, which `dirty` below reports.
  useEffect(() => {
    setOnTime(saved.on || DEFAULT_WINDOW.on);
    setOffTime(saved.off || DEFAULT_WINDOW.off);
  }, [saved.on, saved.off]);

  // With no stored window the scheduler has nothing to follow, so the default
  // shown in the inputs is only a suggestion: it must be savable as-is.
  const hasWindow = Boolean(unit?.schedule?.lights?.on && unit?.schedule?.lights?.off);
  const dirty = !hasWindow
    || onTime !== (saved.on || DEFAULT_WINDOW.on)
    || offTime !== (saved.off || DEFAULT_WINDOW.off);

  const run = async (action) => {
    if (busy) return;
    setBusy(true);
    onError(null);
    try {
      await action();
      await onChanged();
    } catch (err) {
      onError(apiUtils.handleError(err, 'Could not update the lights'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Row dimmed={dimmed} $bare={bare}>
      {/* `bare`: opened under a summary that already says all this. */}
      {bare ? null : (
      <UnitCell>
        <UnitName>{name}</UnitName>
        <StatePill isOn={isOn}>{state}</StatePill>
        {/* Without this a rack reading OFF in timer mode at 14:00 looks broken. */}
        {unit?.heat_break_until && (
          <WindowLabel title="The AC is at full cooling and the container is still hot, so this rack is resting for a few minutes. Turn light breaks off on the Environment page.">
            heat break, back by {clock(unit.heat_break_until)}
          </WindowLabel>
        )}
        <RelayConfirmation report={report} />
      </UnitCell>
      )}

      <ModeSwitch role="group" aria-label={`${name} lights control mode`}>
        <ModeButton
          mode="timer"
          active={mode === 'timer'}
          aria-pressed={mode === 'timer'}
          disabled={dimmed || busy}
          onClick={() => mode !== 'timer' && run(() => hydroUnitsAPI.updateControlMode(unitId, 'lights', 'timer'))}
        >
          Timer
        </ModeButton>
        <ModeButton
          mode="manual"
          active={mode === 'manual'}
          aria-pressed={mode === 'manual'}
          disabled={dimmed || busy}
          onClick={() => mode !== 'manual' && run(() => hydroUnitsAPI.updateControlMode(unitId, 'lights', 'manual'))}
        >
          Manual
        </ModeButton>
      </ModeSwitch>

      <Controls $bare={bare}>
        {mode === 'timer' ? (
          <>
            <TimeInput
              type="time"
              value={onTime}
              disabled={dimmed || busy}
              aria-label={`${name} lights on time`}
              onChange={(e) => setOnTime(e.target.value)}
            />
            <TimeSep>to</TimeSep>
            <TimeInput
              type="time"
              value={offTime}
              disabled={dimmed || busy}
              aria-label={`${name} lights off time`}
              onChange={(e) => setOffTime(e.target.value)}
            />
            <SaveButton
              disabled={dimmed || busy || !dirty}
              onClick={() => run(() => hydroUnitsAPI.updateSchedule(unitId, { lights: { on: onTime, off: offTime } }))}
            >
              {busy ? '…' : 'Save'}
            </SaveButton>
          </>
        ) : (
          <>
            <WindowLabel>on until switched back</WindowLabel>
            <Toggle
              isOn={isOn}
              disabled={dimmed || busy}
              aria-label={`Turn ${name} lights ${isOn ? 'off' : 'on'}`}
              aria-pressed={isOn}
              onClick={() => run(() => hydroUnitsAPI.updateRelay(unitId, { lights: isOn ? 'OFF' : 'ON' }))}
            />
          </>
        )}
      </Controls>

      {mode === 'timer' && (
        <PresetCell>
          <HourPresets
            on={onTime}
            off={offTime}
            onPick={setOffTime}
            disabled={dimmed || busy}
            note={hasWindow ? null : 'not saved, timer idle'}
          />
        </PresetCell>
      )}
    </Row>
  );
};

// Relay writes are refused server-side in demo mode: routing them to demo.db
// would look like it worked, and routing them to the real db would let a demo
// switch a real farm's lights. Say so up front rather than on the first click.
const IS_DEMO = localStorage.getItem('demoMode') === '1';

/* One line per rack: what the light is doing and what decides it. */
const LightSummary = ({ name, unit, report, dimmed, open, onToggle }) => {
  const mode = unit?.control_modes?.lights || 'timer';
  const isOn = unit?.relays?.lights === 'ON';
  const win = unit?.schedule?.lights;
  let when;
  if (unit?.heat_break_until) when = `Heat break, back by ${clock(unit.heat_break_until)}`;
  else if (mode === 'manual') when = 'Manual, stays until switched back';
  else if (win?.on && win?.off) when = `Timer ${win.on} to ${win.off}`;
  else when = 'Timer not set';
  return (
    <Summary type="button" dimmed={dimmed} onClick={onToggle} aria-expanded={open}>
      <UnitName>{name}</UnitName>
      <StatePill isOn={isOn}>{isOn ? 'ON' : 'OFF'}</StatePill>
      <SummaryWhen $manual={mode === 'manual'}>{when}</SummaryWhen>
      <span className="confirm"><RelayConfirmation report={report} /></span>
      <Chevron>{open ? 'Close' : 'Change'}</Chevron>
    </Summary>
  );
};

/* Tiles mode: one small tile per rack saying only what matters at a glance:
   ON / OFF, and whether the timer or a person is in charge. The tile is the
   button that opens that rack's editor underneath. */
const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
  gap: 8px;
`;

const LightTile = styled.button`
  display: block;
  min-width: 0;
  min-height: 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${props => (props.$open ? props.theme.colors.text : props.theme.colors.border)};
  box-shadow: ${props => (props.$open ? `0 0 0 1px ${props.theme.colors.text}` : 'none')};
  background: ${props => (props.$on ? props.theme.colors.successLight : props.theme.colors.surface)};
  font: inherit;
  text-align: left;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  opacity: ${props => (props.dimmed ? 0.45 : 1)};
  transition: border-color 0.12s ease;

  &:hover { border-color: ${props => props.theme.colors.borderHover}; }
  &:focus-visible { outline: 2px solid ${props => props.theme.colors.primary}; outline-offset: 2px; }

  .head { display: flex; justify-content: space-between; align-items: center; gap: 6px; }
  .nm { font-size: 13px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  /* Hours of light a day: the number a grower plans by, so it gets the highlight. */
  .hrs {
    flex: 0 0 auto; font-size: 12px; font-weight: 500; padding: 1px 6px; border-radius: 8px;
    color: ${props => props.theme.colors.textInverse}; background: ${props => props.theme.colors.primary};
  }
  .state { font-size: 20px; font-weight: 500; letter-spacing: -0.02em; color: ${props => (props.$on ? props.theme.colors.success : props.theme.colors.textMuted)}; }
  .mode { margin-top: 2px; font-size: 12px; color: ${props => props.theme.colors.textSecondary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 5px; }
  .mode svg { flex: 0 0 auto; font-size: 13px; color: ${props => (props.$manual ? props.theme.colors.warningHover : props.theme.colors.textMuted)}; }
  /* Is the board that drives this rack reachable. Online is only a green dot beside the
     name (the "since" in its tooltip), so the name keeps the narrow tile's width; anything
     else gets its own line in words, since an ON without a board is only what the server asked. */
  .dot { flex: 0 0 auto; width: 6px; height: 6px; border-radius: 50%; background: ${props => props.theme.colors.success}; }
  .link { margin-top: 2px; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: ${props => props.theme.colors.textMuted}; }
  .link.offline { color: ${props => props.theme.colors.danger}; }
  .row { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
`;

// The icon says who is in charge (clock = timer, hand = a person), so the text
// can be just the window and fit a narrow tile.
function lightMode(unit) {
  const mode = unit?.control_modes?.lights || 'timer';
  const win = unit?.schedule?.lights;
  if (unit?.heat_break_until) return { manual: false, Icon: PiPause, text: `Heat break, back by ${clock(unit.heat_break_until)}`, label: 'heat break' };
  if (mode === 'manual') return { manual: true, Icon: PiHandPointing, text: 'Manual', label: 'manual' };
  if (win?.on && win?.off) {
    const w = windowMinutes(win.on, win.off);
    const hours = w && w.onMinutes ? formatMinutes(w.onMinutes) : null;
    return { manual: false, Icon: PiClock, text: `${win.on} to ${win.off}`, hours, label: `timer ${win.on} to ${win.off}${hours ? `, ${hours} of light a day` : ''}` };
  }
  return { manual: false, Icon: PiClock, text: 'No timer set', label: 'no timer set' };
}

/* The editor for the tile that was tapped: its own card, titled, with Close. */
const Editor = styled.div`
  margin-top: 10px;
  padding: 12px 14px 4px;
  border-radius: 12px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};

  .eh { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 2px; }
  .eh b { font-size: 13px; font-weight: 600; }
  .eh button {
    min-height: 0; padding: 4px 10px; border-radius: 8px; font: inherit; font-size: 12px; font-weight: 500; cursor: pointer;
    border: 1px solid ${props => props.theme.colors.border}; background: ${props => props.theme.colors.surface}; color: ${props => props.theme.colors.text};
  }
  .eh button:hover { background: ${props => props.theme.colors.surfaceHover}; }
`;

const LightsControl = ({ unitNames = {}, compact = false, tiles = false, onUnits }) => {
  const { relayData: socketRelayData } = useSocket();
  const { isUnitEnabled, isUnitInMaintenance } = useUnitSettings();

  const [units, setUnits] = useState({});
  const [reports, setReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  const loadStates = useCallback(async () => {
    try {
      // Commanded state and the controllers' own reports are fetched together so a
      // row can show both what was asked for and whether the hardware agreed.
      // allSettled: a missing relay-status must not blank out the light controls.
      const [overviewRes, statusRes] = await Promise.allSettled([
        hydroUnitsAPI.getRelaysOverview(),
        hydroUnitsAPI.getRelayDeviceStatus(),
      ]);

      if (overviewRes.status === 'rejected') throw overviewRes.reason;

      const byId = {};
      (overviewRes.value.data.units || []).forEach(u => { byId[u.unit_id] = u; });
      setUnits(byId);

      if (statusRes.status === 'fulfilled') {
        const byUnit = {};
        (statusRes.value.data.units || [])
          .filter(u => u.relay === 'lights')
          .forEach(u => { byUnit[u.unit_id] = u; });
        setReports(byUnit);
      }
    } catch (err) {
      setError(apiUtils.handleError(err, 'Could not load light states'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-asked every minute: the socket pushes relay changes, but a controller going
  // quiet produces no event, so its online mark would otherwise never turn red.
  useEffect(() => {
    loadStates();
    const id = setInterval(loadStates, 60 * 1000);
    return () => clearInterval(id);
  }, [loadStates]);

  // The backend scheduler pushes relay_update when a timer fires, so the rows
  // follow the schedule without polling.
  useEffect(() => {
    if (!socketRelayData) return;
    setUnits(prev => {
      const next = { ...prev };
      Object.entries(socketRelayData).forEach(([unitId, update]) => {
        if (!next[unitId]) return;
        next[unitId] = {
          ...next[unitId],
          relays: { ...next[unitId].relays, ...update.relays },
          control_modes: update.control_modes || next[unitId].control_modes,
          timestamp: update.timestamp,
          heat_break_until: update.heat_break ? next[unitId].heat_break_until : null,
        };
      });
      return next;
    });
    // A break just started: the push says so but not until when, so ask once.
    if (Object.values(socketRelayData).some(u => u.heat_break)) loadStates();
  }, [socketRelayData, loadStates]);

  // The parent (Dashboard summary) reads the same live state instead of fetching its own.
  useEffect(() => { if (onUnits) onUnits(units, reports); }, [units, reports, onUnits]);

  const order = id => (SYSTEM_IDS.includes(id) ? SYSTEM_IDS.indexOf(id) : 99);
  const visible = Object.keys(units)
    .filter(isUnitEnabled)
    .sort((a, b) => order(a) - order(b));

  if (loading) return <Note>Loading light states…</Note>;

  return (
    <>
      <Rows>
        {tiles && (
          <TileGrid>
            {visible.map((unitId) => {
              const unit = units[unitId];
              const name = unitNames[unitId] || unit.name || unitId;
              const isOn = unit?.relays?.lights === 'ON';
              const m = lightMode(unit);
              const link = controllerLink(reports[unitId]);
              return (
                <LightTile key={unitId} type="button" $on={isOn} $manual={m.manual} $open={open === unitId}
                  dimmed={IS_DEMO || isUnitInMaintenance(unitId)} aria-expanded={open === unitId}
                  aria-label={`${name}: lights ${isOn ? 'on' : 'off'}, ${m.label}, controller ${link.short.toLowerCase()}. ${open === unitId ? 'Close' : 'Change'}`}
                  onClick={() => setOpen(o => (o === unitId ? null : unitId))}>
                  <div className="head">
                    <span className="nm">{name}</span>
                    {link.state === 'online' && <span className="dot" title={link.title} />}
                  </div>
                  <div className="row">
                    <span className="state">{isOn ? 'ON' : 'OFF'}</span>
                    {m.hours && <span className="hrs" title="Hours of light a day">{m.hours}</span>}
                  </div>
                  <div className="mode"><m.Icon aria-hidden="true" />{m.text}</div>
                  {link.state !== 'online' && <div className={`link ${link.state}`} title={link.title}>{link.word}</div>}
                </LightTile>
              );
            })}
          </TileGrid>
        )}
        {tiles && open && units[open] && (
          <Editor>
            <div className="eh">
              <b>{unitNames[open] || units[open].name || open} lights</b>
              <button type="button" onClick={() => setOpen(null)}>Close</button>
            </div>
            <LightRow unitId={open} name={unitNames[open] || units[open].name || open} unit={units[open]}
              report={reports[open]} dimmed={IS_DEMO || isUnitInMaintenance(open)} onChanged={loadStates} onError={setError} bare />
          </Editor>
        )}
        {compact && !tiles && visible.map(unitId => {
          const name = unitNames[unitId] || units[unitId].name || unitId;
          const dimmed = IS_DEMO || isUnitInMaintenance(unitId);
          return (
            <Item key={unitId}>
              <LightSummary
                name={name}
                unit={units[unitId]}
                report={reports[unitId]}
                dimmed={dimmed}
                open={open === unitId}
                onToggle={() => setOpen(o => (o === unitId ? null : unitId))}
              />
              {open === unitId && (
                <LightRow
                  unitId={unitId}
                  name={name}
                  unit={units[unitId]}
                  report={reports[unitId]}
                  dimmed={dimmed}
                  onChanged={loadStates}
                  onError={setError}
                  bare
                />
              )}
            </Item>
          );
        })}
        {!compact && !tiles && visible.map(unitId => (
          <LightRow
            key={unitId}
            unitId={unitId}
            name={unitNames[unitId] || units[unitId].name || unitId}
            unit={units[unitId]}
            report={reports[unitId]}
            dimmed={IS_DEMO || isUnitInMaintenance(unitId)}
            onChanged={loadStates}
            onError={setError}
          />
        ))}
      </Rows>

      {visible.length === 0 && <Note>No enabled units.</Note>}
      {IS_DEMO && <Note>Light control is disabled in demo mode.</Note>}
      {error && <ErrorNote>{error}</ErrorNote>}
    </>
  );
};

export default LightsControl;
